import { Component } from '@theme/component';

/**
 * A custom element that displays value props in a ticker/marquee format.
 * Only enables marquee animation if content width exceeds viewport width.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} wrapper - The wrapper element.
 * @property {HTMLElement} content - The content element.
 * @property {HTMLElement} tickerItems - The ticker items container.
 *
 * @extends Component<Refs>
 */
class ValuePropsTicker extends Component {
  requiredRefs = ['wrapper', 'content', 'tickerItems'];

  async connectedCallback() {
    super.connectedCallback();

    const { tickerItems } = this.refs;
    if (!tickerItems) return;

    // Check if content overflows viewport on load
    await this.#checkOverflow();
  }

  async #checkOverflow() {
    return new Promise((resolve) => {
      // Use requestAnimationFrame to ensure layout is complete
      requestAnimationFrame(async () => {
        const viewportWidth = this.clientWidth || window.innerWidth;
        const contentWidth = this.refs.tickerItems.scrollWidth;

        if (contentWidth <= viewportWidth) {
          // Content fits - disable marquee, enable centering
          this.setAttribute('data-disabled', 'true');
          this.removeAttribute('data-marquee-active');
          resolve();
        } else {
          // Content overflows - enable marquee
          this.removeAttribute('data-disabled');
          this.setAttribute('data-marquee-active', 'true');

          const numberOfCopies = await this.#queryNumberOfCopies();
          this.#addRepeatedItems(numberOfCopies);
          const speed = this.#calculateSpeed(numberOfCopies);
          this.#duplicateContent();
          this.#setSpeed(speed);
          resolve();
        }
      });
    });
  }

  /**
   * Ensure cloned nodes don't keep `ref` attributes.
   * Horizon refs are name-based (non-array refs override), so cloned refs can
   * unintentionally "steal" this.refs.* and break later logic.
   *
   * @param {Element} root
   */
  #stripRefs(root) {
    root.removeAttribute('ref');
    for (const el of root.querySelectorAll('[ref]')) {
      el.removeAttribute('ref');
    }
  }

  /**
   * @param {number} value
   */
  #setSpeed(value) {
    this.style.setProperty('--ticker-speed', `${value}s`);
  }

  async #queryNumberOfCopies() {
    const { tickerItems } = this.refs;

    return new Promise((resolve) => {
      if (!tickerItems) {
        return setTimeout(() => resolve(1), 0);
      }

      const intersectionObserver = new IntersectionObserver(
        (entries) => {
          const firstEntry = entries[0];
          if (!firstEntry) return;

          intersectionObserver.disconnect();

          const { width: viewportWidth } = firstEntry.rootBounds ?? { width: 0 };
          const { width: itemsWidth } = firstEntry.boundingClientRect;

          setTimeout(() => {
            resolve(itemsWidth === 0 ? 1 : Math.ceil(viewportWidth / itemsWidth));
          }, 0);
        },
        { root: this }
      );
      intersectionObserver.observe(tickerItems);
    });
  }

  /**
   * @param {number} numberOfCopies
   */
  #calculateSpeed(numberOfCopies) {
    const speedFactor = Number(this.getAttribute('data-speed-factor')) || 25;

    const gap = this.refs.wrapper ? parseFloat(getComputedStyle(this.refs.wrapper).gap || '0') : 0;
    const halfWidth = this.refs.content ? this.refs.content.scrollWidth : 0;
    const distancePx = halfWidth + gap / 2;

    // If we can't measure for some reason, fall back to the previous heuristic.
    const fallbackSeconds = Math.sqrt(numberOfCopies) * 20;
    const speed = distancePx > 0 ? distancePx / speedFactor : fallbackSeconds;

    // Keep a sane minimum so short tickers don't feel "hyper fast".
    return Math.max(10, speed);
  }

  #duplicateContent() {
    const clone = /** @type {HTMLElement} */ (this.refs.content.cloneNode(true));

    clone.setAttribute('aria-hidden', 'true');
    this.#stripRefs(clone);

    this.refs.wrapper.appendChild(clone);
  }

  /**
   * @param {number} numberOfCopies
   */
  #addRepeatedItems(numberOfCopies) {
    const { content, tickerItems } = this.refs;

    if (!tickerItems) return;

    for (let i = 0; i < numberOfCopies - 1; i++) {
      const clone = /** @type {HTMLElement} */ (tickerItems.cloneNode(true));
      this.#stripRefs(clone);
      content.appendChild(clone);
    }
  }
}

if (!customElements.get('value-props-ticker')) {
  customElements.define('value-props-ticker', ValuePropsTicker);
}
